import { useState, useRef, useEffect } from 'react';
import { MessageCircle, Send } from 'lucide-react';
import { useTranslation } from '../../i18n';

/**
 * ClassroomChat - Chat panel for Virtual Classroom
 * Supports both individual (1-to-1) and club (group) chat modes
 */
const ClassroomChat = ({
  chatAvailable,
  isClubLesson,
  clubMessages = [],
  messages = [],
  userId,
  onSendMessage,
  onSendClubMessage,
}) => {
  const { t } = useTranslation();
  const [chatMessage, setChatMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const chatEndRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, clubMessages]);

  const handleSend = async () => {
    if (!chatMessage.trim() || isSending) return;

    const text = chatMessage.trim();
    setChatMessage('');
    setIsSending(true);

    try {
      if (isClubLesson) {
        await onSendClubMessage?.(text);
      } else {
        await onSendMessage?.(text);
      }
    } catch (error) {
      // Restore message on error
      setChatMessage(text);
      console.error('Failed to send message:', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Render unavailable state
  if (!chatAvailable) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-4 text-brand-taupe">
        <MessageCircle size={48} className="mb-3 opacity-50" />
        <p className="text-sm font-medium text-center">{t('classroom.chat.unavailable')}</p>
        <p className="text-xs mt-1 text-center">
          {isClubLesson
            ? t('classroom.chat.cantConnectClub')
            : t('classroom.chat.cantConnect')}
        </p>
      </div>
    );
  }

  // Render club group chat messages
  const renderClubMessages = () => {
    if (clubMessages.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-brand-taupe">
          <MessageCircle size={48} className="mb-3 opacity-50" />
          <p className="text-sm">{t('classroom.chat.clubGroup')}</p>
          <p className="text-xs mt-1">{t('classroom.chat.messagesWillAppear')}</p>
        </div>
      );
    }

    return (
      <>
        {clubMessages.map((msg) => {
          const isOwn = String(msg.senderId) === String(userId);
          return (
            <div
              key={msg.id}
              className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-lg px-3 py-2 ${
                  isOwn
                    ? 'bg-brand-primary text-white'
                    : 'bg-brand-light text-brand-primary'
                }`}
              >
                {!isOwn && (
                  <p className="text-xs font-medium text-brand-sage mb-1">{msg.senderName}</p>
                )}
                <p className="text-sm break-words">{msg.text}</p>
                <p className={`text-xs mt-1 ${isOwn ? 'text-white/70' : 'text-brand-taupe'}`}>
                  {msg.time}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={chatEndRef} />
      </>
    );
  };

  // Render individual 1-to-1 chat messages
  const renderIndividualMessages = () => {
    if (messages.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-brand-taupe">
          <MessageCircle size={48} className="mb-3 opacity-50" />
          <p className="text-sm">{t('classroom.chat.lesson')}</p>
          <p className="text-xs mt-1">{t('classroom.chat.messagesWillAppear')}</p>
        </div>
      );
    }

    return (
      <>
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${msg.isOwn ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-lg px-3 py-2 ${
                msg.isOwn
                  ? 'bg-brand-primary text-white'
                  : 'bg-brand-light text-brand-primary'
              }`}
            >
              <p className="text-sm break-words">{msg.text}</p>
              <p className={`text-xs mt-1 ${msg.isOwn ? 'text-white/70' : 'text-brand-taupe'}`}>
                {msg.time}
              </p>
            </div>
          </div>
        ))}
        <div ref={chatEndRef} />
      </>
    );
  };

  return (
    <>
      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {isClubLesson ? renderClubMessages() : renderIndividualMessages()}
      </div>

      {/* Message Input */}
      <div className="p-3 border-t border-brand-light/50">
        <div className="flex gap-2">
          <input
            type="text"
            value={chatMessage}
            onChange={(e) => setChatMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('classroom.chat.inputPlaceholder')}
            className="flex-1 bg-brand-light text-brand-primary rounded-lg px-3 py-2 text-sm placeholder:text-brand-taupe"
          />
          <button
            onClick={handleSend}
            disabled={!chatMessage.trim() || isSending}
            className="bg-brand-primary hover:bg-brand-primary/80 text-white p-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {isSending ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Send size={16} />
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default ClassroomChat;
